import { useRef, useState, type MouseEvent, type PointerEvent } from 'react';
import { api } from '../lib/api';

/**
 * PHONE1 - the Call button, one component, the same on every staff screen (spec 4.1).
 *  - Call in GHL: a REAL link to the person's GoHighLevel page; GoHighLevel's dialer places the call on his GoHighLevel
 *    number, recorded and summarised by Call Intelligence. ⛔ Nothing is awaited between the tap and the open: an iPhone
 *    drops a window opened after an await (BROWSER-VERIFICATION trap 33), so the link is READY before the tap.
 *  - Add to GoHighLevel: no GoHighLevel match, but a phone or an email. One tap creates or matches them; the same spot
 *    then becomes Call in GHL for the second tap. A refusal is said beside it and nothing changes (the icon variant: in a
 *    callout UNDER it that takes no room in the row - LF2).
 *  - Not in GoHighLevel (greyed): nothing to add from. And when the server could not read the link, it says so and
 *    NEVER offers Add (a linked person must not be offered Add). The icon variant says it as a grey icon named by the
 *    words; its screen says the sentence once, above the rows (LF2).
 * ⭐ A tap never reaches a parent: an Opportunity card is draggable and opens its drawer on a click.
 */
export const CALL = 'Call in GHL';
export const ADD = 'Add to GoHighLevel';
export const ADD_SHORT = 'Add to GHL';
/** The icon variant's accessible name: it CONTAINS its visible words, "Add to GHL" (WCAG 2.5.3 label-in-name). */
export const ADD_SHORT_LABEL = 'Add to GHL (GoHighLevel)';
export const ADDING = 'Adding...';
export const NOT_IN_GHL = 'Not in GoHighLevel';
export const UNREAD = "Couldn't check GoHighLevel - reload the page.";
export const NOT_ADDED = "Not added - GoHighLevel didn't answer. Try again.";

export interface CallInGhlProps {
  /** The server's `ghl_contact_url` (null = not linked). */
  url: string | null | undefined;
  /** The APP contact id - needed only to add. */
  contactId?: string | null;
  /** The server's `can_add_to_ghl` (a phone, a mobile phone or an email on file). */
  canAdd?: boolean;
  /** The server's `ghl_link_unread`. */
  unread?: boolean;
  /** 'icon' = the escrow rows' small phone button. */
  variant?: 'button' | 'icon';
  /**
   * Told the new page once, after a successful Add - so the screen holding the person can mark them linked - with the
   * server's note and the contact the Add was FOR. ⛔ A note means the page is ANOTHER person's ("already has this phone
   * or email on <who>"): never copy it onto this person as their own link. ⛔ The Add can answer after the screen has
   * moved on to someone else: patch only the contact whose id this is.
   */
  onAdded?: (url: string, note: string | null, contactId: string) => void;
}

const stop = (e: MouseEvent | PointerEvent) => e.stopPropagation();

const PhoneIcon = () => (
  <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M4 4h4l2 5-2 1a12 12 0 006 6l1-2 5 2v4a1 1 0 01-1 1A17 17 0 013 5a1 1 0 011-1z" />
  </svg>
);

const BUTTON = 'inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-sm font-medium transition-colors';
const LIVE = 'border-gray-300 bg-white text-gray-900 hover:border-gray-900';
const GREY = 'border-gray-200 bg-gray-50 text-gray-400 cursor-default';
const ICON = 'flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-500 transition-colors hover:border-gray-900 hover:text-gray-900';
const ICON_ADD = 'flex h-7 items-center rounded-lg border border-gray-300 px-2 text-[11px] font-medium text-gray-700 hover:border-gray-900 hover:text-gray-900 disabled:opacity-50';
const ICON_GREY = 'flex h-7 w-7 items-center justify-center rounded-lg border border-gray-200 text-gray-300 cursor-default';
const WRAP = 'inline-flex flex-wrap items-center gap-2';
// LF2: the escrow rows' action group never shrinks - inline words ran off a 390 px screen and pushed the button over the
// party's name. The icon variant's words sit in a callout under the button, out of the row's flow.
const ICON_WRAP = 'relative inline-flex';
const ICON_CALLOUT = 'absolute right-0 top-full z-10 mt-1 w-56 max-w-[70vw] rounded-md border border-gray-200 bg-white p-2 text-left text-xs leading-snug shadow-sm flex items-start gap-2';
const ICON_DISMISS = '-mr-0.5 -mt-0.5 shrink-0 rounded px-1 leading-none text-gray-400 hover:text-gray-900';

export default function CallInGhl({ url, contactId, canAdd = false, unread = false, variant = 'button', onAdded }: CallInGhlProps) {
  // ⛔ The added page and the words are held WITH the contact they were for: whenever this same instance is handed another
  //    person (a list that reorders, a row React reuses), they must never get the first person's page, note or refusal.
  const [added, setAdded] = useState<{ contactId: string; url: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const [words, setWords] = useState<{ contactId: string; kind: 'note' | 'error'; text: string } | null>(null);
  const inFlight = useRef(false);
  const icon = variant === 'icon';
  const href = url || (added && added.contactId === contactId ? added.url : null);
  const said = words && words.contactId === contactId ? words : null;

  // LF2 (review round 1): the callout lies over the NEXT party's buttons (the lane's rows are ~37 px apart), and after a
  // note nothing else would ever clear it - a tap on its words, or on its Dismiss mark, closes it. Round 3: the words are
  // a real BUTTON - on an iPhone a tap on plain text in the box never reached its handler, while the "x" (a button) always
  // did. The box carries the role (still announced) and holds every tap, padding included: none ever reaches the row.
  const dismiss = (e: MouseEvent) => {
    e.stopPropagation();
    setWords(null);
  };
  const role = said?.kind === 'error' ? 'alert' : 'status';
  const tone = said?.kind === 'error' ? 'text-red-700' : 'text-gray-600';
  const line = said && (icon ? (
    <span role={role} onClick={stop} onPointerDown={stop} className={ICON_CALLOUT}>
      <button type="button" onClick={dismiss} onPointerDown={stop} className={`min-w-0 flex-1 text-left cursor-pointer ${tone}`}>{said.text}</button>
      <button type="button" aria-label="Dismiss" onClick={dismiss} onPointerDown={stop} className={ICON_DISMISS}>x</button>
    </span>
  ) : (
    <span role={role} className={`text-xs ${tone}`}>{said.text}</span>
  ));

  const add = async (e: MouseEvent) => {
    e.stopPropagation();
    if (inFlight.current || !contactId) return;
    const forId = contactId;
    inFlight.current = true;
    setBusy(true);
    setWords(null);
    try {
      const r = await api.addContactToGhl(forId);
      if (r.ghl_contact_url) {
        setAdded({ contactId: forId, url: r.ghl_contact_url });
        if (r.note) setWords({ contactId: forId, kind: 'note', text: r.note });
        onAdded?.(r.ghl_contact_url, r.note ?? null, forId);
      } else {
        // A "success" with no page leaves nothing to call - say so; never sit silently on Add.
        setWords({ contactId: forId, kind: 'error', text: NOT_ADDED });
      }
    } catch (err) {
      setWords({ contactId: forId, kind: 'error', text: err instanceof Error && err.message ? err.message : NOT_ADDED });
    } finally {
      inFlight.current = false;
      setBusy(false);
    }
  };

  if (href) {
    return (
      <span className={icon ? ICON_WRAP : WRAP}>
        <a href={href} target="_blank" rel="noreferrer" onClick={stop} onPointerDown={stop} aria-label={CALL}
          title="Call (opens GoHighLevel)" className={icon ? ICON : `${BUTTON} ${LIVE}`}>
          {icon ? <PhoneIcon /> : <><PhoneIcon />{CALL}</>}
        </a>
        {line}
      </span>
    );
  }

  if (unread) {
    // LF2: in a row the sentence (~275 px) pushed every party's buttons over the names at once - a grey icon instead.
    if (icon) {
      return (
        <span aria-disabled="true" role="img" aria-label={UNREAD} title={UNREAD} onClick={stop} onPointerDown={stop} className={ICON_GREY}>
          <PhoneIcon />
        </span>
      );
    }
    return <span role="status" className="text-xs text-gray-500">{UNREAD}</span>;
  }

  if (contactId && canAdd) {
    return (
      <span className={icon ? ICON_WRAP : WRAP}>
        {/* The button variant's name is its visible text; the icon variant's name contains its visible "Add to GHL". */}
        <button type="button" onClick={add} onPointerDown={stop} disabled={busy} aria-label={icon ? (busy ? ADDING : ADD_SHORT_LABEL) : undefined}
          title="Add to GoHighLevel, then call" className={icon ? ICON_ADD : `${BUTTON} ${LIVE} disabled:opacity-50`}>
          {busy ? ADDING : icon ? ADD_SHORT : ADD}
        </button>
        {line}
      </span>
    );
  }

  return (
    <span aria-disabled="true" role={icon ? 'img' : undefined} aria-label={icon ? NOT_IN_GHL : undefined}
      title="No phone or email to add" onClick={stop} onPointerDown={stop} className={icon ? ICON_GREY : `${BUTTON} ${GREY}`}>
      {icon ? <PhoneIcon /> : NOT_IN_GHL}
    </span>
  );
}
